/* eslint-disable camelcase */
// --- Request Processors

import { globalState, localeUrl, parseJSON } from '@/components/helpers/utils'
import { recordMarketPrices } from '@/components/helpers/market-price'
import { getProductPrices } from '../../storefront/products'

/**
 *
 * @param {Facet[]} facets
 * @returns {NormalisedFacet[]}
 */
export function normaliseFacets(facets) {
  return facets.map((facet) => {
    return {
      ...facet,
      values: facet.values.filter((item) => item.value !== '"')
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Sorting[]} options
 * @returns {NormalisedSorting[]}
 */
export function normaliseSortOptions(options) {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {Swatch[]}
 */
function getFirstImageUrl(item) {
  if (!item) return null
  if (typeof item === 'string' && item.length > 0) return item
  if (typeof item === 'object') return item.url ?? item.src ?? null
  return null
}

function parseDelimitedList(raw) {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'string') return []

  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    const jsonParsed = parseJSON(trimmed, 'array')
    if (Array.isArray(jsonParsed) && jsonParsed.length > 0) return jsonParsed
  }

  if (raw.includes('|')) return raw.split('|')
  if (raw.includes(',')) return raw.split(',')
  return [raw]
}

export function normaliseSwatches(result) {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings, 'array')
  if (!parsedSwatches) return []
  const publishedSwatches = parsedSwatches.filter((swatch) => swatch.published)

  const swatchesWithImages = publishedSwatches.map((swatch) => {
    const image =
      getFirstImageUrl(swatch.images?.[0]) ?? getFirstImageUrl(swatch.image) ?? getFirstImageUrl(swatch.featured_image) ?? null
    return {
      ...swatch,
      url: swatch.handle ? localeUrl(`products/${swatch.handle}`) : result.url,
      ...(image && { image })
    }
  })

  return swatchesWithImages.sort((swatchA, swatchB) => {
    if (swatchA.isCurrentProduct === swatchB.isCurrentProduct) return 0
    if (swatchA.isCurrentProduct) return -1
    return 1
  })
}

/**
 * Parse the comma-separated sibling handles field from Searchspring.
 * Returns the full list of sibling product handles (unfiltered, deduped).
 * @param {Result} result
 * @returns {string[]}
 */
export function normaliseSiblingHandles(result) {
  const raw = result?.mfield_dotapparel21_siblings_handles
  const list = parseDelimitedList(raw)
    .map((handle) => (typeof handle === 'string' ? handle.trim().toLowerCase() : null))
    .filter(Boolean)
  return Array.from(new Set(list))
}

/**
 * Normalise delimited string/array types into arrays
 * @param {String|Array} result
 * @returns {Array}
 */
export function normalisedArray(result) {
  if (result?.includes('|')) {
    return result?.split('|')
  } else if (typeof result === 'string') {
    return [result]
  } else {
    return result
  }
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {NormalisedImage[]}
 */
export function normaliseImages(result) {
  const { images, images_alt } = result
  const srcs = normalisedArray(images)
  const alts = normalisedArray(images_alt)

  return srcs?.map((src, index) => {
    return {
      src,
      alt: alts ? alts[index] : ''
    }
  })
}

/**
 * Adjust pagination totals after client-side filtering so that
 * result counts and page numbers stay consistent with what is rendered.
 */
export function adjustPagination(apiResponse, normalisedResults) {
  const filteredCount = (apiResponse.results?.length ?? 0) - normalisedResults.length

  if (!apiResponse.pagination || filteredCount <= 0) {
    return { ...apiResponse, results: normalisedResults }
  }

  const { totalResults, totalPages } = apiResponse.pagination
  const adjustedTotal = Math.max(0, totalResults - filteredCount)
  const perPage = Math.ceil(totalResults / (totalPages || 1)) || 1

  return {
    ...apiResponse,
    results: normalisedResults,
    pagination: {
      ...apiResponse.pagination,
      totalResults: adjustedTotal,
      totalPages: Math.max(1, Math.ceil(adjustedTotal / perPage))
    }
  }
}

/**
 * Keep only products tagged as in-stock for the current market.
 * Tags follow the convention: Flow:InStock:{ISO_COUNTRY_CODE}
 * Used for recommendation carousels where server-side bgfilter is unavailable.
 * @param {Result[]} results
 * @returns {Result[]}
 */
function filterMarketInStock(results) {
  const market = globalState?.localization?.market
  if (!market) return results

  const inStockTag = `Flow:InStock:${market}`

  return results.filter((result) => {
    const tags = normalisedArray(result.tags) ?? []
    return tags.includes(inStockTag)
  })
}

/**
 * Apply B2B pricing to search results
 * @param {NormalisedResult[]} results
 * @returns {NormalisedResult[]}
 */
function applyB2BPricing(results) {
  // Check if customer is B2B and B2B pricing is available
  const isB2BCustomer = globalState?.customer?.b2b
  const b2bPricing = globalState?.customer?.b2bPricing

  if (!isB2BCustomer || !b2bPricing) {
    return results
  }

  return results.map((result) => {
    const productId = result.uid || result.id?.toString?.() || result.id
    const b2bData = b2bPricing[productId]

    if (b2bData && b2bData.price) {
      const updatedVariants = result.variants.map((variant) => ({
        ...variant,
        price: b2bData.price.toString(),
        compare_at_price: variant.price || result.price
      }))

      const updatedResult = {
        ...result,
        price: b2bData.price.toString(),
        variants: updatedVariants
      }

      return updatedResult
    }

    return result
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result[]} results
 * @returns {Promise[NormalisedResult[]]}
 */
export async function normaliseResults(results) {
  if (!results) return []
  const normalisedResults = results?.map((result) => {
    const swatches = normaliseSwatches(result)
    const siblingHandles = normaliseSiblingHandles(result)
    const images = normaliseImages(result)
    const variants = parseJSON(result.variants, 'array')
    return {
      ...result,
      images,
      swatches,
      siblingHandles,
      variants
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  const b2bResults = applyB2BPricing(normalisedResults)

  if (requiresPriceLocalisation()) {
    return await localiseResults(b2bResults)
  }
  return b2bResults
}

/**
 * Whether feed prices need a Storefront lookup to match the active market.
 *
 * A different presentment currency always needs one. So does a market that shares
 * the shop's currency but can still carry its own price list — only the shop's
 * own country is guaranteed to match the base prices the SearchSpring feed was
 * generated from.
 */
export function requiresPriceLocalisation() {
  const { localization, shopify } = globalState
  if (!localization?.country) return false
  if (localization.currency && shopify?.currency && localization.currency !== shopify.currency) return true

  return Boolean(shopify?.country) && localization.country !== shopify.country
}

async function localiseResults(normalisedResults) {
  const localisedPrices = await getProductPrices(normalisedResults)
  const pricePairs = []

  const localisedResults = normalisedResults.map((result) => {
    const priceMatch = localisedPrices?.find((product) => {
      return product.id.split('/').pop() === result.uid
    })

    if (priceMatch) {
      pricePairs.push([Number(result.price), Number(priceMatch.price)])

      const resultVariants = (result.variants || []).map((variant) => {
        return {
          ...variant,
          price: priceMatch.price
        }
      })
      return {
        ...result,
        variants: resultVariants,
        price: priceMatch.price
      }
    } else {
      return result
    }
  })

  recordMarketPrices(pricePairs)

  return localisedResults
}

/**
 * Normalise blog article results from the SearchSpring API
 * @param {Result[]} results
 * @returns {NormalisedBlogResult[]}
 */
export async function normaliseBlogResults(results) {
  if (!results) return []
  return results.map((result) => ({
    id: result.uid,
    title: result.name,
    url: result.url,
    excerpt: result.description,
    image: result.thumbnailImageUrl ? { src: result.thumbnailImageUrl, alt: result.name } : null,
    published_at: result.published_at,
    author: result.author,
    tags: result.tags,
    blog_handle: result.blog_handle
  }))
}

/**
 *
 * @param {Result[]} results
 * @returns {NormalisedResult[]}
 */
export async function normaliseRecommendations(results) {
  const normalisedResults = results?.results?.map((result) => {
    const swatches = normaliseSwatches(result.attributes)
    const siblingHandles = normaliseSiblingHandles(result.attributes)
    const variants = parseJSON(result.attributes.variants, 'array')
    const images = normaliseImages(result.attributes)

    return {
      ...result,
      ...result.attributes,
      ...result.mappings.core,
      images,
      variants,
      swatches,
      siblingHandles
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  const marketFilteredResults = filterMarketInStock(normalisedResults)
  if (!marketFilteredResults.length) return []

  const b2bResults = applyB2BPricing(marketFilteredResults)

  if (requiresPriceLocalisation()) {
    return await localiseResults(b2bResults)
  }

  return b2bResults
}
